import React, { useEffect, useMemo } from "react";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";

interface WorkflowFilters {
  audienceType: string;
  actionType: string;
  status: string;
}

interface WorkflowsFilterProps {
  audienceType: string;
  actionType: string;
  status: string;
  audienceTypeList: Array<{ label: string; value: string }>;
  actionTypeList: Array<{ label: string; value: string }>;
  statusList: Array<{ label: string; value: string }>;
  onApplyFilters: (filters: WorkflowFilters) => void;
  onResetFilters: () => void;
}

export const WorkflowsFilter: React.FC<WorkflowsFilterProps> = ({
  audienceType,
  actionType,
  status,
  audienceTypeList,
  actionTypeList,
  statusList,
  onApplyFilters,
  onResetFilters,
}) => {
  // Transform lists to options format
  const audienceTypeOptions = useMemo(
    () =>
      audienceTypeList?.map((item) => ({
        value: item.value,
        label: item.label,
      })) || [],
    [audienceTypeList]
  );

  const actionTypeOptions = useMemo(
    () =>
      actionTypeList?.map((item) => ({
        value: item.value,
        label: item.label,
      })) || [],
    [actionTypeList]
  );

  const statusOptions = useMemo(
    () =>
      statusList?.map((item) => ({
        value: item.value,
        label: item.label,
      })) || [],
    [statusList]
  );

  // Define filter fields
  const filterFields: FormField[] = useMemo(
    () => [
      {
        name: "audienceType",
        label: "Audience Type",
        type: "select",
        placeholder: "Select Audience Type",
        options: audienceTypeOptions,
        className: "col-span-1",
        classes: {
          container: "w-full mb-6",
        },
      },
      {
        name: "actionType",
        label: "Action Type",
        type: "select",
        placeholder: "Select Action Type",
        options: actionTypeOptions,
        className: "col-span-1",
        classes: {
          container: "w-full mb-6",
        },
      },
      {
        name: "status",
        label: "Status",
        type: "select",
        placeholder: "Select Status",
        options: statusOptions,
        className: "col-span-1",
        classes: {
          container: "w-full mb-6",
        },
      },
    ],
    [audienceTypeOptions, actionTypeOptions, statusOptions]
  );

  // Create form instance
  const form = FormService.createForm({
    fields: filterFields,
    mode: "onChange",
    validateOnMount: false,
  });

  // Sync form values when URL params change
  useEffect(() => {
    form.reset({
      audienceType: decodeURIComponent(audienceType) || "",
      actionType: decodeURIComponent(actionType) || "",
      status: decodeURIComponent(status) || "",
    });
  }, [audienceType, actionType, status, form]);

  const handleFilterSubmit = () => {
    const formValues = form.getValues();
    onApplyFilters({
      audienceType: formValues.audienceType || "",
      actionType: formValues.actionType || "",
      status: formValues.status || "",
    });
  };

  const handleFilterReset = () => {
    form.reset({
      audienceType: "",
      actionType: "",
      status: "",
    });
    onResetFilters();
  };

  const handleFieldChange = (field: string, value: string) => {
    // Add any field-specific logic here if needed
  };

  return (
    <div>
      <DynamicForm
        fields={filterFields}
        form={form}
        onSubmit={handleFilterSubmit}
        onReset={handleFilterReset}
        onFieldChange={handleFieldChange}
        gridColumns={3}
        config={{
          layout: "vertical",
          showSubmitButton: true,
          showResetButton: true,
          submitButtonText: "Apply Filters",
          resetButtonText: "Clear",
          classes: {
            buttonContainer: "flex justify-end gap-4",
          },
        }}
      />
    </div>
  );
};
